import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StudentNavigation from '../../components/ui/StudentNavigation';
import NotificationCenter from '../../components/ui/NotificationCenter';
import SessionStatusIndicator from '../../components/ui/SessionStatusIndicator';
import PanoramicViewer from './components/PanoramicViewer';
import NavigationPanel from './components/NavigationPanel';
import HotspotModal from './components/HotspotModal';
import GuidedTourControls from './components/GuidedTourControls';
import Button from '../../components/ui/Button';

import { instituteService } from '../../services/instituteService';
import { validationUtils } from '../../utils/validation';

const CampusTour360 = () => {
  const navigate = useNavigate();
  const { id: instituteId } = useParams();
  const [notifications, setNotifications] = useState([
  {
    id: 1,
    type: 'system',
    title: 'Welcome to Campus Tour',
    message: 'Click on hotspots to explore buildings and facilities',
    timestamp: new Date(Date.now() - 60000),
    read: false
  }]
  );

  const [currentLocation, setCurrentLocation] = useState('main-entrance');
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [isGuidedTour, setIsGuidedTour] = useState(false);
  const [guidedTourStep, setGuidedTourStep] = useState(0);
  const [viewAngle, setViewAngle] = useState({ yaw: 0, pitch: 0 });
  const [zoom, setZoom] = useState(1);
  const [showNavigationPanel, setShowNavigationPanel] = useState(false);
  const [visitedLocations, setVisitedLocations] = useState(['main-entrance']);
  const [campusLocations, setCampusLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [instituteName, setInstituteName] = useState('');
  const [tourBlocked, setTourBlocked] = useState(false);

  // Fetch campus tour data from database
  useEffect(() => {
    const fetchCampusTourData = async () => {
      // Validate institute ID before making any API calls
      if (!instituteId) {
        setError('Institute ID is required');
        setLoading(false);
        return;
      }

      // Check if the ID is a valid UUID format
      if (!validationUtils?.isValidUUID(instituteId)) {
        console.error('Invalid UUID format for institute ID:', instituteId);
        setError('Invalid institute ID format. Please return to the exhibition hall and try again.');
        setLoading(false);
        setTimeout(() => navigate('/exhibition-hall', { replace: true }), 3000);
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching campus tour for institute ID:', instituteId);
        
        // First, get the institute data to get the user_id
        const instituteData = await instituteService?.getInstituteForBooth(instituteId);
        
        if (!instituteData) {
          console.error('Institute not found for ID:', instituteId);
          setError('Institute not found');
          setLoading(false);
          return;
        }

        console.log('Institute data:', instituteData);

        // Check if 360 tour is activated by super admin
        if (!instituteData?.tour_360_enabled) {
          setTourBlocked(true);
          setInstituteName(instituteData?.institute_name || instituteData?.name || 'this institute');
          setLoading(false);
          return;
        }

        // Get institute name
        setInstituteName(instituteData?.institute_name || instituteData?.name || 'Campus Tour');

        // Get campus tour URLs from virtual_booths data
        const virtualBoothData = instituteData?.virtual_booths?.[0];
        
        if (!virtualBoothData) {
          console.error('No virtual booth data found for institute:', instituteId);
          setError('Virtual booth not found for this institute');
          setLoading(false);
          return;
        }

        console.log('Virtual booth data:', virtualBoothData);
        
        const campusTourUrls = virtualBoothData?.campus_tour_urls || [];
        
        console.log('Campus tour URLs:', campusTourUrls);

        if (campusTourUrls?.length === 0) {
          setError('No campus tour data available for this institute. The institute needs to add 360° tour URLs from their dashboard.');
          setLoading(false);
          return;
        }

        // Transform database data to component format
        const transformedLocations = campusTourUrls?.map((tour, index) => ({
          id: tour?.id || `location-${index}`,
          name: tour?.name || `Location ${index + 1}`,
          description: tour?.description || '',
          panoramaUrl: tour?.panorama_url || tour?.url || '',
          panoramaAlt: tour?.alt || `Panoramic view of ${tour?.name || 'campus location'}`,
          coordinates: tour?.coordinates || { x: 50, y: 50 },
          hotspots: tour?.hotspots || []
        }));

        console.log('Transformed locations:', transformedLocations);

        setCampusLocations(transformedLocations);
        
        // Set first location as current
        if (transformedLocations?.length > 0) {
          setCurrentLocation(transformedLocations?.[0]?.id);
          setVisitedLocations([transformedLocations?.[0]?.id]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching campus tour data:', err);
        setError(`Failed to load campus tour data: ${err?.message || 'Unknown error'}`);
        setLoading(false);
      }
    };

    fetchCampusTourData();
  }, [instituteId]);

  const guidedTourSequence = campusLocations?.map((loc) => ({
    locationId: loc?.id,
    narration: loc?.description || `Explore ${loc?.name}`
  }));

  const currentLocationData = campusLocations?.find((loc) => loc?.id === currentLocation);

  const handleHotspotClick = (hotspot) => {
    setSelectedHotspot(hotspot);
  };

  const handleNavigateToLocation = (locationId) => {
    setCurrentLocation(locationId);
    setViewAngle({ yaw: 0, pitch: 0 });
    setZoom(1);
    if (!visitedLocations?.includes(locationId)) {
      setVisitedLocations([...visitedLocations, locationId]);
    }
    setShowNavigationPanel(false);
  };

  const handleStartGuidedTour = () => {
    if (guidedTourSequence?.length > 0) {
      setIsGuidedTour(true);
      setGuidedTourStep(0);
      handleNavigateToLocation(guidedTourSequence?.[0]?.locationId);
    }
  };

  const handleNextTourStep = () => {
    if (guidedTourStep < guidedTourSequence?.length - 1) {
      const nextStep = guidedTourStep + 1;
      setGuidedTourStep(nextStep);
      handleNavigateToLocation(guidedTourSequence?.[nextStep]?.locationId);
    } else {
      setIsGuidedTour(false);
      setGuidedTourStep(0);
    }
  };

  const handlePreviousTourStep = () => {
    if (guidedTourStep > 0) {
      const prevStep = guidedTourStep - 1;
      setGuidedTourStep(prevStep);
      handleNavigateToLocation(guidedTourSequence?.[prevStep]?.locationId);
    }
  };

  const handleStopGuidedTour = () => {
    setIsGuidedTour(false);
    setGuidedTourStep(0);
  };

  const handleExploreFromHotspot = (linkedLocation) => {
    if (linkedLocation) {
      handleNavigateToLocation(linkedLocation);
      setSelectedHotspot(null);
    }
  };

  const handleVisitVirtualBooth = () => {
    // Navigate to the specific institute's virtual booth
    navigate(`/virtual-booth/${instituteId}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <StudentNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading campus tour...</p>
          </div>
        </div>
      </div>
    );
  }

  // Tour not activated by super admin
  if (tourBlocked) {
    return (
      <div className="min-h-screen bg-background">
        <StudentNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-6 mb-6">
              <div className="w-16 h-16 bg-warning/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-warning">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <p className="font-semibold text-foreground text-lg mb-2">360° Tour Not Activated</p>
              <p className="text-sm text-muted-foreground">
                The 360° Campus Tour for <span className="font-medium text-foreground">{instituteName}</span> has not been activated yet. Please contact the platform admin to enable this feature.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                iconName="ArrowLeft"
              >
                Go Back
              </Button>
              <Button
                variant="default"
                onClick={() => navigate(`/virtual-booth/${instituteId}`)}
                iconName="Store"
              >
                Visit Virtual Booth
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <StudentNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-4">
              <p className="font-semibold mb-2">Campus Tour Not Available</p>
              <p className="text-sm">{error}</p>
            </div>
            <Button
              variant="default"
              onClick={() => navigate('/student-dashboard')}
              iconName="ArrowLeft"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No locations available
  if (campusLocations?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <StudentNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md mx-auto px-4">
            <p className="text-muted-foreground mb-4">No campus tour locations available</p>
            <Button
              variant="default"
              onClick={() => navigate('/student-dashboard')}
              iconName="ArrowLeft"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StudentNavigation />
      
      <div className="flex flex-col lg:flex-row">
        <main className="flex-1 lg:ml-64">
          <div className="sticky top-0 z-30 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                360° Campus Tour - {instituteName}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {currentLocationData?.name} - {visitedLocations?.length} of {campusLocations?.length} locations explored
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Map"
                onClick={() => setShowNavigationPanel(!showNavigationPanel)}>

                <span className="hidden md:inline">Map</span>
              </Button>
              {!isGuidedTour && campusLocations?.length > 1 &&
              <Button
                variant="default"
                size="sm"
                iconName="Navigation"
                onClick={handleStartGuidedTour}>

                  <span className="hidden md:inline">Guided Tour</span>
                </Button>
              }
            </div>
          </div>

          <div className="relative">
            <PanoramicViewer
              location={currentLocationData}
              viewAngle={viewAngle}
              zoom={zoom}
              onViewChange={setViewAngle}
              onZoomChange={setZoom}
              onHotspotClick={handleHotspotClick} />


            {isGuidedTour &&
            <GuidedTourControls
              currentStep={guidedTourStep}
              totalSteps={guidedTourSequence?.length}
              narration={guidedTourSequence?.[guidedTourStep]?.narration}
              onNext={handleNextTourStep}
              onPrevious={handlePreviousTourStep}
              onStop={handleStopGuidedTour} />

            }

            {showNavigationPanel &&
            <NavigationPanel
              locations={campusLocations}
              currentLocation={currentLocation}
              visitedLocations={visitedLocations}
              onNavigate={handleNavigateToLocation}
              onClose={() => setShowNavigationPanel(false)} />

            }
          </div>

          <div className="p-4 md:p-6 bg-card border-t border-border">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-3">
                {currentLocationData?.name}
              </h2>
              <p className="text-muted-foreground mb-4">
                {currentLocationData?.description}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Building2"
                  onClick={handleVisitVirtualBooth}>

                  Visit Virtual Booth
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageSquare"
                  onClick={() => navigate('/chat-interface')}>

                  Chat with Admissions
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Bookmark">

                  Save Location
                </Button>
              </div>
            </div>
          </div>
        </main>

        <aside className="lg:w-80 border-l border-border">
          <div className="sticky top-0">
            <NotificationCenter
              notifications={notifications}
              onNotificationRead={(id) => {
                setNotifications(notifications?.map((n) =>
                n?.id === id ? { ...n, read: true } : n
                ));
              }}
              onNotificationDismiss={(id) => {
                setNotifications(notifications?.filter((n) => n?.id !== id));
              }} />

            <SessionStatusIndicator
              onChatClick={() => navigate('/chat-interface')}
              onVideoClick={() => navigate('/video-chat')} />

          </div>
        </aside>
      </div>

      {selectedHotspot &&
      <HotspotModal
        hotspot={selectedHotspot}
        onClose={() => setSelectedHotspot(null)}
        onExplore={handleExploreFromHotspot}
        onVisitBooth={handleVisitVirtualBooth} />

      }
    </div>);

};

export default CampusTour360;